import React, { useState, useMemo, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Search, Filter, ChevronDown, ChevronRight, FileText, Calendar, DollarSign, User, Package, Clock, Tag } from 'lucide-react';
import QuotationDashboard from './quotation/QuotationDashboard';
import QuotationList from './quotation/QuotationList';
import QuotationDetail from './quotation/QuotationDetail';
import SalesOrderView from './SalesOrderView';

const QuotationView = () => {
  // State management
  const [activeView, setActiveView] = useState('dashboard'); // dashboard, list, detail
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('issue_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedQuotation, setExpandedQuotation] = useState(null);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [quotationStatuses, setQuotationStatuses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch quotation statuses from Supabase
  const fetchQuotationStatuses = async () => {
    try {
      const { data: statusesData, error: statusesError } = await supabase
        .from('quotation_statuses')
        .select('*')
        .order('id');

      if (statusesError) {
        console.error('Error fetching quotation statuses:', statusesError);
        return;
      }

      // Transform the data to match our expected format
      const transformedStatuses = statusesData.map(status => ({
        status_id: status.id,
        status_name: status.name,
        status_name_en: status.name_en || status.name,
        description: status.description,
        color_code: status.color,
        icon: status.icon,
        sort_order: status.sort_order || 0
      }));

      setQuotationStatuses(transformedStatuses || []);
    } catch (error) {
      console.error('Error fetching quotation statuses:', error);
    }
  };

  // Fetch quotations data
  const fetchQuotations = async () => {
    try {
      setLoading(true);
      
      // Fetch quotations with customer data
      const { data: quotationsData, error: quotationsError } = await supabase
        .from('quotations')
        .select(`
          *,
          customers!quotations_customer_id_fkey (
            id,
            name,
            contact_person,
            email,
            phone
          )
        `)
        .order('created_at', { ascending: false });

      if (quotationsError) {
        console.error('Error fetching quotations:', quotationsError);
        return;
      }

      // Fetch quotation items for all quotations
      const { data: quotationItemsData, error: itemsError } = await supabase
        .from('quotation_items')
        .select('*')
        .order('created_at', { ascending: false });

      if (itemsError) {
        console.error('Error fetching quotation items:', itemsError);
        return;
      }

      // Transform the data to match the expected format
      const transformedQuotations = quotationsData.map(quotation => {
        // Find items for this quotation
        const quotationItems = quotationItemsData.filter(item => item.quotation_id === quotation.id);
        
        // Transform items to match the expected format
        const transformedItems = quotationItems.map(item => ({
          product_name: item.description || '未知產品',
          description: item.description || '',
          quantity: item.quantity || 0,
          unit: '件',
          unit_price: item.unit_price || 0,
          tax_rate: 0, // Default tax rate
          subtotal: item.total_amount || 0,
          tax_amount: 0, // Default tax amount
          total: item.total_amount || 0,
          discount_percentage: item.discount_percentage || 0
        }));

        return {
          ...quotation,
          customer_name: quotation.customers?.name || '未知客戶',
          contact_person: quotation.customers?.contact_person || quotation.contact_person,
          contact_email: quotation.customers?.email || quotation.contact_email,
          contact_phone: quotation.customers?.phone || quotation.contact_phone,
          // Add items array with the fetched quotation items
          items: transformedItems
        };
      });

      setQuotations(transformedQuotations);
      
    } catch (error) {
      console.error('Error fetching quotations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchQuotations();
    fetchQuotationStatuses();
  }, []);

  // Helper functions
  const getStatusDetails = (statusId) => {
    const status = quotationStatuses.find(s => s.status_id === statusId);
    return status || {
      status_name: '未知',
      color_code: '#6B7280',
      icon: 'HelpCircle'
    };
  };

  const getCustomerDetails = (customerId) => {
    return customers.find(customer => customer.id === customerId) || {
      name: '未知客戶',
      contact_person: '未知',
      email: '',
      phone: ''
    };
  };

  const getProductDetails = (productId) => {
    return products.find(product => product.id === productId) || {
      name: '未知產品',
      description: '',
      base_price: 0
    };
  };

  // Filtered and sorted quotations
  const filteredQuotations = useMemo(() => {
    return quotations.filter(quotation => {
      // Apply search term filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        quotation.quotation_number?.toLowerCase().includes(searchLower) ||
        quotation.customer_name?.toLowerCase().includes(searchLower) ||
        quotation.sales_rep_name?.toLowerCase().includes(searchLower);
      
      // Apply status filter
      const matchesStatus = statusFilter === 'all' || quotation.status_id === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [quotations, searchTerm, statusFilter]);

  const sortedQuotations = useMemo(() => {
    return [...filteredQuotations].sort((a, b) => {
      let comparison = 0;
      
      if (sortField === 'issue_date') {
        comparison = new Date(a.issue_date || 0) - new Date(b.issue_date || 0);
      } else if (sortField === 'expiry_date') {
        comparison = new Date(a.expiry_date || 0) - new Date(b.expiry_date || 0);
      } else if (sortField === 'total_amount') {
        comparison = (a.total_amount || 0) - (b.total_amount || 0);
      } else if (sortField === 'customer_name') {
        comparison = (a.customer_name || '').localeCompare(b.customer_name || '');
      } else {
        comparison = (a[sortField] || '').localeCompare(b[sortField] || '');
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredQuotations, sortField, sortDirection]);

  // Event handlers
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleViewQuotation = (quotation) => {
    setSelectedQuotation(quotation);
    setActiveView('detail');
  };

  const handleQuotationUpdate = (updatedQuotation) => {
    // Update the quotation in the local state
    setQuotations(prevQuotations => 
      prevQuotations.map(q => 
        q.id === updatedQuotation.id ? updatedQuotation : q
      )
    );
    
    // Update the selected quotation if it's the same one
    if (selectedQuotation && selectedQuotation.id === updatedQuotation.id) {
      setSelectedQuotation(updatedQuotation);
    }
    
    // Refresh data from database to ensure consistency
    setTimeout(() => {
      fetchQuotations();
    }, 1000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading quotations...</div>
      </div>
    );
  }

  // Render content based on active view
  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return (
          <QuotationDashboard 
            sortedQuotations={sortedQuotations}
            quotationStatuses={quotationStatuses}
            getStatusDetails={getStatusDetails}
            handleViewQuotation={handleViewQuotation}
            setActiveView={setActiveView}
          />
        );
      case 'list':
        return (
          <QuotationList
            sortedQuotations={sortedQuotations}
            expandedQuotation={expandedQuotation}
            setExpandedQuotation={setExpandedQuotation}
            getStatusDetails={getStatusDetails}
            handleViewQuotation={handleViewQuotation}
            setActiveView={setActiveView}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            sortField={sortField}
            sortDirection={sortDirection}
            handleSort={handleSort}
            quotationStatuses={quotationStatuses}
          />
        );
      case 'detail':
        return (
          <QuotationDetail
            quotation={selectedQuotation}
            getStatusDetails={getStatusDetails}
            getCustomerDetails={getCustomerDetails}
            setActiveView={setActiveView}
            onQuotationUpdate={handleQuotationUpdate}
          />
        );
      case 'sales-order':
        return (
          <SalesOrderView />
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      {/* Navigation buttons for testing */}
      <div className="mb-4 flex space-x-4">
        <button 
          onClick={() => setActiveView('dashboard')}
          className={`px-4 py-2 rounded-lg ${activeView === 'dashboard' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
        >
          儀表板
        </button>
        <button 
          onClick={() => setActiveView('list')}
          className={`px-4 py-2 rounded-lg ${activeView === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
        >
          報價單列表
        </button>
      </div>
      
      {renderContent()}
    </div>
  );
};

export default QuotationView;
